<template>
  <TransitionRoot as="template" :show="open">
    <Dialog class="relative z-50" @close="closeDialog">
      <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
      </TransitionChild>

      <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
            <DialogPanel class="relative transform overflow-hidden rounded-lg bg-white px-8 pb-8 pt-6 text-left shadow-xl transition-all sm:my-8 sm:w-full lg:max-w-5xl sm:p-6">
              
              <div class="flex justify-between items-start border-b pb-4">
                <h2 class="text-xl font-bold leading-9 tracking-tight text-gray-900">{{ title }}</h2>
                <button @click="closeDialog" type="button" class="text-gray-400 hover:text-gray-600">
                  <span class="sr-only">Fechar</span>
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>

              <div class="mt-4 grid grid-cols-1 md:grid-cols-2 gap-6">
                
                <!-- Coluna Esquerda: Dados do Modelo -->
                <div class="space-y-4">
                    <div>
                      <label class="block text-sm font-semibold leading-6 text-gray-900">Nome do Modelo *</label>
                      <input v-model="form.nome_modelo" required class="mt-2 w-full rounded-md border-0 py-1.5 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-customPurple sm:text-sm" />
                    </div>

                    <div>
                      <label class="block text-sm font-semibold leading-6 text-gray-900">Plano Vinculado *</label>
                      <input
                        v-model="planoBusca"
                        @input="buscarPlanos"
                        type="text"
                        placeholder="Digite 3+ letras para buscar o plano..."
                        class="mt-2 w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-customPurple sm:text-sm"
                      />
                      <ul
                        v-if="sugestoesPlanos.length"
                        class="mt-1 border border-gray-300 rounded-md bg-white shadow-lg p-2 max-h-40 overflow-y-auto z-20 relative"
                      >
                        <li
                          v-for="p in sugestoesPlanos"
                          :key="p.id"
                          @click="selecionaPlano(p)"
                          class="cursor-pointer hover:bg-gray-100 p-2 rounded text-sm text-gray-700 font-semibold"
                        >
                          {{ p.nome }}
                        </li>
                      </ul>
                      <div v-if="form.plano_id" class="mt-2 text-sm font-semibold text-customPurple">
                        Plano Selecionado: {{ nomePlanoSelecionado }}
                        <button type="button" @click="limparPlano" class="ml-2 text-xs text-red-500 underline">Limpar</button>
                      </div>
                    </div>

                    <div>
                      <label class="block text-sm font-semibold leading-6 text-gray-900">Versão</label>
                      <input v-model="form.versao" placeholder="Ex: v1.0" class="mt-2 w-full rounded-md border-0 py-1.5 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-customPurple sm:text-sm" />
                    </div>

                    <div>
                      <label class="block text-sm font-semibold leading-6 text-gray-900">Arquivo DOCX *</label>
                      <input type="file" ref="fileInput" accept=".docx" @change="handleFileUpload" class="mt-2 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100" />
                      <p v-if="fileName" class="text-xs text-green-600 mt-1">Carregado: {{ fileName }}</p>
                    </div>

                    <!-- Botão de Preview -->
                    <div class="pt-4 border-t mt-6 flex gap-3">
                         <button type="button" @click="handlePreview" :disabled="!isReadyForPreview" class="flex-1 justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:bg-gray-400">
                            🔍 Gerar Pré-Visualização
                         </button>
                    </div>

                </div>

                <!-- Coluna Direita: Regras, Blacklist e Extra Fields (Deparas) -->
                <div class="space-y-4">
                  
                  <div class="bg-gray-50 border p-3 rounded-md text-sm text-gray-700 h-96 overflow-y-auto">
                     <h3 class="font-bold text-red-600 mb-2">⚠ Placeholders Nativos (NÃO USE!)</h3>
                     <p class="text-xs mb-2 leading-relaxed">O sistema da Justech já substitui magicamente os seguintes termos no Word (utilize-os exatamente como estão):</p>
                     
                     <div class="space-y-2 text-xs">
                        <p><strong>Proponente (Dados do Segurado):</strong> {{'{'+'{'}}nome{{'}'+'}'}}, {{'{'+'{'}}cpf{{'}'+'}'}}, {{'{'+'{'}}rg{{'}'+'}'}}, {{'{'+'{'}}data_nascimento{{'}'+'}'}}, {{'{'+'{'}}render{{'}'+'}'}}, {{'{'+'{'}}profissao{{'}'+'}'}}, {{'{'+'{'}}telefone{{'}'+'}'}}, {{'{'+'{'}}email{{'}'+'}'}}, {{'{'+'{'}}endereco_rua{{'}'+'}'}}, {{'{'+'{'}}endereco_cidade{{'}'+'}'}}, {{'{'+'{'}}end_uf{{'}'+'}'}}, {{'{'+'{'}}end_cep{{'}'+'}'}} ...</p>
                        <p><strong>Cônjuge:</strong> {{'{'+'{'}}conjuge_nome{{'}'+'}'}}, {{'{'+'{'}}cpf_conjuge{{'}'+'}'}}, {{'{'+'{'}}data_nascimento_conjuge{{'}'+'}'}}, {{'{'+'{'}}telefone_conjuge{{'}'+'}'}} ...</p>
                        <p><strong>Perguntas Visita:</strong> {{'{'+'{'}}1{{'}'+'}'}} ao {{'{'+'{'}}19{{'}'+'}'}} e {{'{'+'{'}}24{{'}'+'}'}} ao {{'{'+'{'}}26{{'}'+'}'}}</p>
                        <p><strong>Cobrança:</strong> {{'{'+'{'}}numero_banco{{'}'+'}'}}, {{'{'+'{'}}numero_agencia{{'}'+'}'}}, {{'{'+'{'}}conta_corrente{{'}'+'}'}}, {{'{'+'{'}}periodicidade{{'}'+'}'}}, {{'{'+'{'}}data_vencimento_cobranca{{'}'+'}'}}</p>
                        <p><strong>Beneficiários (1 a 6):</strong> {{'{'+'{'}}beneficiario_1_nome{{'}'+'}'}}, {{'{'+'{'}}cpf_bene_1{{'}'+'}'}}, {{'{'+'{'}}parentesco_bene_1{{'}'+'}'}}, {{'{'+'{'}}participa_bene_1{{'}'+'}'}}</p>
                     </div>

                     <!-- Coberturas Vinculadas -->
                     <div class="mt-4 border-t pt-4">
                        <div class="flex items-center justify-between mb-2">
                           <h3 class="font-bold text-indigo-600">🛡 Coberturas do Plano</h3>
                           <button type="button" @click="addCobertura" class="text-xs px-2 py-1 bg-indigo-100 text-indigo-700 rounded hover:bg-indigo-200" :disabled="!form.plano_id">
                             + Nova
                           </button>
                        </div>
                        <p class="text-xs italic mb-2">Desenhe as chaves no Word para renderizar o nome destas coberturas:</p>
                        
                        <div v-if="coberturasPlanos.length === 0" class="text-xs text-gray-500 italic p-2 bg-white rounded border border-dashed border-gray-300">
                           Nenhuma cobertura atrelada a este plano. Adicione se necessário.
                        </div>

                        <ul class="space-y-2 max-h-48 overflow-y-auto pr-1">
                          <li v-for="(c, cIndex) in coberturasPlanos" :key="cIndex" class="flex items-center gap-2 text-xs bg-white p-2 rounded shadow-sm border border-gray-100">
                             <input v-model="c.chave_placeholder" placeholder="{{chave}}" class="w-1/3 border-gray-300 rounded py-1 px-2 font-mono text-indigo-700" />
                             <span class="text-gray-400">=</span>
                             <input v-model="c.nome" placeholder="Nome da Cobertura" class="flex-1 border-gray-300 rounded py-1 px-2 text-gray-800" />
                             <button type="button" @click="removeCobertura(cIndex)" class="text-red-400 hover:text-red-600">
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
                             </button>
                          </li>
                        </ul>
                     </div>
                  </div>

                </div>

              </div>

             <!-- SESSÃO DE DEPARAS (PERGUNTAS CUSTOMIZADAS) -->
              <div class="mt-8 pt-4 border-t border-gray-200">
                    <div class="flex items-center justify-between mb-4">
                        <h3 class="text-lg font-bold text-gray-900">Perguntas Personalizadas (Depara)</h3>
                        <button type="button" @click="addDepara" class="text-sm px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 shadow-sm">+ Adicionar Pergunta</button>
                    </div>

                    <div v-if="form.deparas.length === 0" class="text-sm text-gray-500 italic text-center py-4 bg-gray-50 rounded-md">
                        Nenhuma pergunta personalizada cadastrada neste modelo. Use apenas se o Formulário Padrão da Visita não cobrir sua necessidade!
                    </div>

                     <div v-for="(depara, index) in form.deparas" :key="index" class="flex flex-wrap items-end gap-3 mb-4 p-3 border border-gray-200 rounded-md bg-gray-50 relative">
                        <!-- Pergunta Label Frontend -->
                        <div class="flex-1 min-w-[200px]">
                           <label class="block text-xs font-semibold text-gray-700">O que deseja perguntar?</label>
                           <input v-model="depara.pergunta" required placeholder="Ex: Qual o valor do Auxílio..." class="mt-1 w-full text-sm border-gray-300 rounded-md py-1" />
                        </div>
                        
                        <!-- Chave de Replace DOCX -->
                        <div class="w-1/4 min-w-[150px]">
                           <label class="block text-xs font-semibold text-gray-700">Chave Alvo DOCX *</label>
                           <input v-model="depara.chave_placeholder" required placeholder="{{minha_chave}}" class="mt-1 w-full text-sm border-gray-300 rounded-md py-1 text-blue-700 font-mono" />
                        </div>

                        <!-- Tipo -->
                        <div class="w-1/5 min-w-[120px]">
                           <label class="block text-xs font-semibold text-gray-700">Tipo Campo</label>
                           <select v-model="depara.tipo" class="mt-1 w-full text-sm border-gray-300 rounded-md py-1">
                              <option value="valor">Texto Comum</option>
                              <option value="checkbox_sim_nao">Sim / Não (Checkbox/String)</option>
                           </select>
                        </div>

                        <button type="button" @click="removeDepara(index)" class="mb-1 text-red-500 hover:text-red-700" title="Remover">
                           <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                        </button>
                     </div>
              </div>

              <!-- BOTÕES DA MODAL -->
              <div class="mt-6 flex items-center justify-end gap-x-4 border-t pt-4">
                <button type="button" @click="closeDialog" class="text-sm font-semibold leading-6 text-gray-900">Cancelar</button>
                <button type="button" @click="submitForm" :disabled="!isReadyForSave" class="rounded-md bg-customPurple px-6 py-2 text-sm font-semibold text-white shadow-sm hover:bg-sienaBlue disabled:bg-gray-400">
                  {{ isEdit ? 'Salvar Alterações' : 'Criar Modelo' }}
                </button>
              </div>

            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script>
import { Dialog, DialogPanel, TransitionChild, TransitionRoot } from '@headlessui/vue';
import PlanosRepository from '@/services/PlanosRepository.vue';
import ModeloPropostaRepository from '@/services/ModeloPropostaRepository.vue';
import Swal from 'sweetalert2';
import debounce from 'lodash/debounce';
import { handleUnauthorized } from '@/services/AuthSer/authServiceFunction';

export default {
  name: 'ModelosPropostaForm',
  components: { Dialog, DialogPanel, TransitionChild, TransitionRoot },
  props: {
    open: Boolean,
    isEdit: Boolean,
    title: String,
    placeHolderValues: Object
  },
  data() {
    return {
      onlineUser: {},
      coberturasPlanos: [],
      sugestoesPlanos: [],
      planoBusca: '',
      nomePlanoSelecionado: '',
      fileName: '',
      form: {
        nome_modelo: '',
        plano_id: '',
        versao: 'v1.0',
        base: null,
        deparas: []
      }
    };
  },
  created() {
    this.onlineUser = JSON.parse(window.localStorage.getItem("myUser") || "{}");
    this.buscarPlanos = debounce((...args) => {
       this._buscarPlanos(...args);
    }, 400);

    if (this.isEdit && this.placeHolderValues.modelo) {
      this.populateEditData();
    }
  },
  computed: {
    isReadyForPreview() {
      return this.form.plano_id && this.form.base;
    },
    isReadyForSave() {
      if (this.isEdit) {
         return this.form.nome_modelo && this.form.plano_id;
      }
      return this.form.nome_modelo && this.form.plano_id && this.form.base;
    }
  },
  methods: {
    async _buscarPlanos() {
       if (!this.planoBusca || this.planoBusca.length < 3) {
           this.sugestoesPlanos = [];
           return;
       }
       const repo = new PlanosRepository();
       try {
          const resp = await repo.getPlanoQuery(this.onlineUser.token, this.planoBusca);
          if (resp?.status === 200) {
             this.sugestoesPlanos = resp.data;
          }
       } catch (error) {
           handleUnauthorized(error);
       }
    },
    selecionaPlano(plano) {
       this.form.plano_id = plano.id;
       this.nomePlanoSelecionado = plano.nome;
       this.planoBusca = '';
       this.sugestoesPlanos = [];
       // Also populate the side-rules if the search payload brings 'coberturas' array
       this.coberturasPlanos = plano.coberturas || [];
    },
    limparPlano() {
       this.form.plano_id = '';
       this.nomePlanoSelecionado = '';
       this.coberturasPlanos = [];
    },
    populateEditData() {
      const mod = this.placeHolderValues.modelo;
      this.form.nome_modelo = mod.nome_modelo;
      this.form.plano_id = mod.plano_id;
      this.nomePlanoSelecionado = this.placeHolderValues.plano_nome || 'Plano Vinculado'; // Pre-fetch name
      this.form.versao = mod.versao;
      // Preenche os deparas devolvidos pela API
      if (this.placeHolderValues.deparas) {
          const apiDeparas = this.placeHolderValues.deparas;
          this.form.deparas = apiDeparas.map(d => ({
              pergunta: d.pergunta,
              chave_placeholder: d.chave_placeholder,
              tipo: d.tipo
          }));
      }
      // Assuming 'plano' object doesn't come directly from list anymore, 
      // they might have to do another search to see coberturas when editing, 
      // or we can fetch only the coberturas for this plan on mount:
      this.fetchCoberturasDoPlanoSalvo(mod.plano_id);
    },
    async fetchCoberturasDoPlanoSalvo(id) {
       if(!id) return;
       const repo = new PlanosRepository();
       try {
          const resp = await repo.getPlano(this.onlineUser.token, id);
          if(resp?.status === 200 && resp.data.coberturas) {
             this.coberturasPlanos = resp.data.coberturas;
          }
       } catch(e) {
         handleUnauthorized(e);
       }
    },
    handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      const validExts = ['application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!validExts.includes(file.type) && !file.name.endsWith('.docx')) {
         Swal.fire('Formato Inválido', 'Por favor, selecione um arquivo em formato Word (.docx).', 'warning');
         this.$refs.fileInput.value = '';
         return;
      }

      this.fileName = file.name;
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
         this.form.base = reader.result;
      };
      reader.onerror = (error) => {
         console.error('File reading failed:', error);
         Swal.fire('Erro', 'Não foi possível ler o arquivo do Word.', 'error');
      };
    },
    addCobertura() {
       this.coberturasPlanos.push({
           nome: '',
           chave_placeholder: '{{nova_chave_cobertura}}'
       });
    },
    removeCobertura(index) {
       this.coberturasPlanos.splice(index, 1);
    },
    addDepara() {
        this.form.deparas.push({
            pergunta: '',
            chave_placeholder: '{{nova_chave}}',
            tipo: 'valor',
            ordem: this.form.deparas.length,
            obrigatorio: false
        });
    },
    removeDepara(index) {
        this.form.deparas.splice(index, 1);
    },
    async handlePreview() {
      const repo = new ModeloPropostaRepository();
      try {
         Swal.fire({ title: 'Gerando Preview', text: 'Preenchendo documento...', allowOutsideClick: false, showConfirmButton: false });
         Swal.showLoading();

         // Fix coberturas wrappers before sending
         const payloadCoberturas = this.coberturasPlanos.map(c => {
             let chave = c.chave_placeholder.trim();
             if (!chave.startsWith('{{')) chave = '{{' + chave;
             if (!chave.endsWith('}}')) chave = chave + '}}';
             return { nome: c.nome, chave_placeholder: chave };
         });

         const resp = await repo.previewModelo(this.onlineUser.token, {
             base: this.form.base,
             plano_id: this.form.plano_id,
             deparas: this.form.deparas,
             coberturas: payloadCoberturas
         });

         if (resp?.status === 200) {
            Swal.close();
            // Download mechanism
            const base64Data = resp.data.base64;
            const link = document.createElement("a");
            link.href = base64Data;
            link.download = `preview_modelo_${this.form.nome_modelo}.docx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            Swal.fire('Preview Gerado', 'O Documento preenchido deve estar baixando em seu computador.', 'success');
         } else if (resp?.status === 422) {
            Swal.fire('Erro de Colisão', resp.data?.mensagem + '\nChaves Bloqueadas: ' + resp.data?.chaves_invalidas.join(','), 'error');
         } else {
            Swal.fire('Erro', 'Falha inexperada no Preview.', 'error');
         }
      } catch (e) {
         Swal.fire('Erro', 'Houve um erro no processamento do Preview DOCX.', 'error');
      }
    },
    submitForm(e) {
        e.preventDefault();
        
        // Remove whitespace and forces {{ }} wrapping
        const payloadDepara = this.form.deparas.map(d => {
            let chave = d.chave_placeholder.trim();
            if (!chave.startsWith('{{')) chave = '{{' + chave;
            if (!chave.endsWith('}}')) chave = chave + '}}';
            return {
               pergunta: d.pergunta,
               chave_placeholder: chave,
               tipo: d.tipo,
               ordem: d.ordem,
               obrigatorio: d.obrigatorio
            }
        });

        const payloadCoberturas = this.coberturasPlanos.map(c => {
            let chave = c.chave_placeholder.trim();
            if (!chave.startsWith('{{')) chave = '{{' + chave;
            if (!chave.endsWith('}}')) chave = chave + '}}';
            return { nome: c.nome, chave_placeholder: chave };
        });

        const payload = {
            nome_modelo: this.form.nome_modelo,
            plano_id: this.form.plano_id,
            versao: this.form.versao,
            deparas: payloadDepara,
            coberturas: payloadCoberturas
        };

        if (this.form.base) {
           payload.base = this.form.base;
        }

        if (this.isEdit) {
            this.$emit('editing-data', payload, this.placeHolderValues.modelo.id);
        } else {
            this.$emit('data', payload);
        }
    },
    closeDialog() {
      this.$emit('update-open', false);
    }
  }
}
</script>
